import {
  Translation,
  TranslocoLoader,
  TranslocoModule,
  provideTransloco
} from "@jsverse/transloco";
import { inject, Injectable, NgModule } from "@angular/core";
import { environment } from "src/environments/environment";
import { provideTranslocoMessageformat } from "@jsverse/transloco-messageformat";
import { BabelBackend } from "src/app/backend/api/babel.backend";
import { defer, forkJoin, from, lastValueFrom, map } from "rxjs";
import { TRANSLATIONS_TEMP } from "src/app/transloco/transloco-temp-translations";

@Injectable({ providedIn: "root" })
export class TranslocoHttpLoader implements TranslocoLoader {
  private readonly babelBackend = inject(BabelBackend);

  async getTranslation(lang: string): Promise<Translation> {
    switch (lang) {
      case "it": {
        return await lastValueFrom(
          forkJoin({
            base: this.babelBackend.load("it-IT"),
            diagnostic: this.babelBackend.loadDiagnostic("it-IT")
          }).pipe(
            map(({ base, diagnostic }) => {
              return {
                ...base,
                ...diagnostic,
                ...TRANSLATIONS_TEMP.it
              };
            })
          )
        );
      }
      case "en": {
        return await Promise.all([
          fetch("/assets/i18n/en.json").then((res) => res.json()),
          lastValueFrom(this.babelBackend.load("en-US")),
          lastValueFrom(this.babelBackend.loadDiagnostic("en-US"))
        ]).then(([en, babelBaseResponse, babelDiagnosticResponse]) => {
          return {
            ...en,
            ...babelBaseResponse,
            ...babelDiagnosticResponse,
            ...TRANSLATIONS_TEMP.en
          };
        });
      }
      case "es": {
        return await Promise.all([
          fetch("/assets/i18n/es.json").then((res) => res.json()),
          lastValueFrom(this.babelBackend.load("es-ES")),
          lastValueFrom(this.babelBackend.loadDiagnostic("es-ES"))
        ]).then(([assets, babelBaseResponse, babelDiagnosticResponse]) => {
          return {
            ...assets,
            ...babelBaseResponse,
            ...babelDiagnosticResponse
          };
        });
      }
      case "fr": {
        return await lastValueFrom(
          forkJoin({
            base: this.babelBackend.load("fr-FR"),
            diagnostic: this.babelBackend.loadDiagnostic("fr-FR")
          }).pipe(
            map(({ base, diagnostic }) => {
              return {
                ...base,
                ...diagnostic,
                ...TRANSLATIONS_TEMP.fr
              };
            })
          )
        );
      }
      case "ka": {
        return await Promise.all([
          fetch("/assets/i18n/en.json").then((res) => res.json()),
          lastValueFrom(this.babelBackend.load("en-US")),
          lastValueFrom(this.babelBackend.loadDiagnostic("en-US"))
        ]).then(([en, babelBaseResponse, babelDiagnosticResponse]) => {
          return lastValueFrom(
            forkJoin({
              local: from(
                defer(() =>
                  fetch("/assets/i18n/ka.json").then((response) =>
                    response.json()
                  )
                )
              )
            }).pipe(
              map(({ local }) => {
                return {
                  ...en,
                  ...babelBaseResponse,
                  ...babelDiagnosticResponse,
                  ...TRANSLATIONS_TEMP.en,
                  ...local
                };
              })
            )
          );
        });
      }
      default: {
        return Promise.resolve({});
      }
    }
  }
}

@NgModule({
  exports: [TranslocoModule],
  providers: [
    provideTransloco({
      config: {
        availableLangs: environment.snoots.languages.supported,
        reRenderOnLangChange: false,
        prodMode: environment.snoots.production,
        missingHandler: {
          allowEmpty: true,
          logMissingKey: false
        }
      },
      loader: TranslocoHttpLoader
    }),
    provideTranslocoMessageformat({
      locales: environment.snoots.languages.supported
    })
  ]
})
export class TranslocoRootModule {}
